import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { ContractDto } from "@rentablo/api/contracts";
import { useMyself } from "@api";
import {
  useContractForms,
  useContractTransactionsData,
  useGetContractCategories,
} from "@features/contractAnalyzer/hooks";
import { getContractCategories } from "@features/contractAnalyzer/utils";
import { ContractSubCategory } from "@features/contractAnalyzer/types";
import { ContractEditTarget } from "../ContractAnalyzerProvider";
import { useEditNavigation } from "./useEditNavigation";

const providerNameOf = (contract: ContractDto | undefined) =>
  contract?.userDefinedProvider ?? contract?.autoAssignedProvider?.name ?? "";

export type ContractEditContextProps = ReturnType<typeof useContractEditModel>;

const ContractEditContext = createContext<ContractEditContextProps | null>(
  null
);

export const useContractEditContext = (): ContractEditContextProps => {
  const ctx = useContext(ContractEditContext);
  if (!ctx) {
    throw new Error(
      "useContractEditContext must be used inside ContractEditProvider"
    );
  }
  return ctx;
};

interface Props {
  target: ContractEditTarget;
  onClose: () => void;
  children: ReactNode;
}

export const ContractEditProvider = ({ target, onClose, children }: Props) => {
  const model = useContractEditModel({ target, onClose });
  return (
    <ContractEditContext.Provider value={model}>
      {children}
    </ContractEditContext.Provider>
  );
};

const useContractEditModel = ({
  target,
  onClose,
}: {
  target: ContractEditTarget;
  onClose: () => void;
}) => {
  const { initialMode, splitTransactions: initialSplitTxns } = target;
  const { data: userData, isFetching: isLoadingUser } = useMyself({
    refetchOnMount: false,
  });
  const { contractForm, secondContractForm } = useContractForms(
    target.contract
  );
  const { data: categories, isFetching: isLoadingCategories } =
    useGetContractCategories();

  const [contract, setContract] = useState<ContractDto>(target.contract);
  const [secondContract, setSecondContract] = useState<
    ContractDto | undefined
  >();
  const [subCategory, setSubCategory] = useState<ContractSubCategory>(() =>
    initialMode === "splitIntoNew"
      ? "notClassified"
      : getContractCategories(contract).subCategory
  );
  const [splitSelection, setSplitSelection] = useState<string[]>(
    initialMode === "splitIntoNew" ? initialSplitTxns : []
  );

  const {
    transactions,
    totalTransactions,
    isInternalTransfer,
    splitSelectionTransactions,
    transactionIds,
    isLoading: isLoadingTransactions,
  } = useContractTransactionsData({
    contractId: contract.id,
    userId: userData?.id,
    isLoadingUser,
    splitSelection,
  });

  const providerVal = contractForm.watch("provider");
  const subCategoryNum =
    typeof subCategory === "number" ? subCategory : undefined;

  const navigation = useEditNavigation({
    target,
    contract,
    secondContract,
    setSubCategory,
    setSplitSelection,
    setSecondContract,
    secondContractForm,
    onClose,
  });

  const toggleSplitSelection = (id: string) =>
    setSplitSelection((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleSelectSecondContract = (selected: ContractDto) => {
    setSecondContract(selected);
    secondContractForm.reset({ provider: providerNameOf(selected) });
    navigation.setView({ name: "merge", step: "main" });
  };

  useEffect(() => {
    if (initialMode === "splitIntoNew") {
      secondContractForm.reset({ provider: providerNameOf(contract) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed split form once on mount
  }, []);

  return {
    ...navigation,

    contract,
    setContract,
    secondContract,
    setSecondContract,
    subCategory,
    setSubCategory,
    splitSelection,
    setSplitSelection,
    splitSelectionTransactions,
    toggleSplitSelection,
    handleSelectSecondContract,

    contractForm,
    secondContractForm,
    categories,
    isLoadingCategories,

    userId: userData?.id,
    transactions,
    isInternalTransfer,
    transactionIds,
    totalTransactions,
    isLoadingTransactions,
    providerVal,
    subCategoryNum,
    onClose,
  };
};
